import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "../../App";
import { Clock, Sparkles, User, ChevronDown, ChevronUp, RefreshCw, Copy, Check, Loader2, MessageSquare, Mail, FileText, BookOpen, Building2, Send, Edit, PenLine } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";

export const TicketCard = ({ ticket, isExpanded, onToggle }) => {
  const [draft, setDraft] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [showConversations, setShowConversations] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedDraft, setEditedDraft] = useState("");
  const [showDirectEmail, setShowDirectEmail] = useState(false);
  const [directEmailContent, setDirectEmailContent] = useState("");

  const getPriorityClass = (priority) => {
    switch (priority?.toUpperCase()) {
      case "HIGH":
        return "priority-high";
      case "MEDIUM":
        return "priority-medium";
      case "LOW":
        return "priority-low";
      default:
        return "priority-medium";
    }
  };

  const getStatusClass = (status) => {
    const s = status?.toLowerCase();
    if (s === "1" || s === "open" || s === "new") return "status-open";
    if (s === "2" || s === "pending" || s === "waiting") return "status-pending";
    if (s === "3" || s === "escalated") return "status-escalated";
    if (s === "4" || s === "closed" || s === "resolved") return "status-resolved";
    return "status-open";
  };

  const getStatusLabel = (status, statusLabel) => {
    if (statusLabel) return statusLabel;
    const s = status?.toLowerCase();
    if (s === "1") return "New";
    if (s === "2") return "Waiting on contact";
    if (s === "3") return "Waiting on us";
    if (s === "4") return "Closed";
    return status || "New";
  };

  const handleGenerateDraft = async () => {
    setDraftLoading(true);
    try {
      const response = await axios.post(
        `${API}/ai/draft-response`,
        {
          ticket_id: ticket.ticket_id,
          subject: ticket.subject,
          description: ticket.description,
          ticket_type: ticket.ticket_type,
          include_context: true  // Enable conversation history and KB context
        },
        { withCredentials: true }
      );
      setDraft(response.data);
      if (response.data.requires_human) {
        toast.info("This ticket requires human attention");
      } else {
        const contextInfo = response.data.context_used;
        let message = "AI draft generated";
        if (contextInfo?.conversation_history || contextInfo?.knowledge_base) {
          message += " with";
          if (contextInfo.conversation_history) message += " conversation history";
          if (contextInfo.conversation_history && contextInfo.knowledge_base) message += " and";
          if (contextInfo.knowledge_base) message += " KB articles";
        }
        toast.success(message);
      }
    } catch (error) {
      toast.error("Failed to generate draft");
      // Error handled silently
    } finally {
      setDraftLoading(false);
    }
  };

  const fetchConversations = async () => {
    if (conversations.length > 0) {
      setShowConversations(!showConversations);
      return;
    }
    
    setConversationsLoading(true);
    try {
      const response = await axios.get(
        `${API}/hubspot/tickets/${ticket.ticket_id}/conversations`,
        { withCredentials: true }
      );
      setConversations(response.data.conversations || []);
      setShowConversations(true);
      if (response.data.conversations?.length === 0) {
        toast.info("No conversation history found");
      }
    } catch (error) {
      toast.error("Failed to load conversations");
      // Error handled silently
    } finally {
      setConversationsLoading(false);
    }
  };

  const copyToClipboard = () => {
    const textToCopy = isEditing ? editedDraft : draft?.draft;
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSendEmail = async (emailContent = null) => {
    const bodyToSend = emailContent || (isEditing ? editedDraft : draft?.draft);
    
    if (!bodyToSend || !ticket.contact_email) {
      toast.error("Cannot send: missing email content or contact email");
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/email/send-direct`,
        {
          contact_email: ticket.contact_email,
          contact_id: ticket.contact_id,
          ticket_id: ticket.ticket_id,
          subject: `Re: ${ticket.subject || "Your Support Request"}`,
          body: bodyToSend,
          source: "ticket"
        },
        { withCredentials: true }
      );
      
      if (response.data.requires_manual_send) {
        toast.info("Email logged in HubSpot - please send manually from HubSpot inbox");
      } else {
        toast.success("Email sent successfully!");
      }
      setDraft({ ...draft, draft: bodyToSend, sent: true });
      setIsEditing(false);
      setShowDirectEmail(false);
      setDirectEmailContent("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
      // Error handled silently
    } finally {
      setSending(false);
    }
  };

  const startEditing = () => {
    setEditedDraft(draft?.draft || "");
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditedDraft("");
  };

  const handleSendDirectEmail = () => {
    if (!directEmailContent.trim()) {
      toast.error("Please enter email content");
      return;
    }
    handleSendEmail(directEmailContent);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`card-hover rounded-xl border transition-all bg-white ${
        ticket.priority?.toUpperCase() === "HIGH"
          ? "border-red-200 shadow-[0_0_20px_rgba(239,68,68,0.1)]"
          : "border-slate-200"
      } shadow-sm`}
    >
      {/* Header */}
      <div
        className="p-5 cursor-pointer"
        onClick={onToggle}
        data-testid={`ticket-${ticket.ticket_id}`}
      >
        <div className="flex items-start gap-4">
          {/* Priority indicator */}
          <div className={`w-2.5 h-2.5 rounded-full mt-2 ${getPriorityClass(ticket.priority)}`} />
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <span className="mono text-xs text-slate-400">#{ticket.ticket_id}</span>
              <Badge className={`${getStatusClass(ticket.status)} rounded-md text-xs font-medium px-2 py-0.5`}>
                {getStatusLabel(ticket.status, ticket.status_label)}
              </Badge>
              {ticket.ticket_type && (
                <Badge variant="outline" className="text-xs border-slate-200 text-slate-500">
                  {ticket.ticket_type}
                </Badge>
              )}
            </div>
            
            <h3 className="text-slate-900 font-medium text-base mb-2 line-clamp-1">
              {ticket.subject || "No Subject"}
            </h3>
            
            {/* Customer/Company Tags */}
            {(ticket.contact_name || ticket.company || ticket.user_type) && (
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {ticket.contact_name && ticket.contact_name !== "Unknown" && (
                  <Badge variant="outline" className="text-xs border-slate-200 text-slate-600 gap-1">
                    <User className="w-3 h-3" />
                    {ticket.contact_name}
                  </Badge>
                )}
                {ticket.company && (
                  <Badge variant="outline" className="text-xs border-blue-200 text-blue-600 bg-blue-50 gap-1">
                    <Building2 className="w-3 h-3" />
                    {ticket.company}
                  </Badge>
                )}
                {ticket.user_type && (
                  <Badge className={`text-xs gap-1 ${
                    ticket.user_type.toLowerCase() === 'manager' 
                      ? 'bg-purple-100 text-purple-700 border-purple-200' 
                      : 'bg-teal-100 text-teal-700 border-teal-200'
                  }`}>
                    {ticket.user_type.toLowerCase() === 'manager' ? 'Manager' : 'Provider'}
                  </Badge>
                )}
              </div>
            )}
            
            <div className="flex items-center gap-4 text-sm text-slate-400">
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                <span>
                  {ticket.created_date
                    ? new Date(ticket.created_date).toLocaleDateString()
                    : "Unknown"}
                </span>
              </div>
              {ticket.resolution_time_hours && (
                <div className="flex items-center gap-1.5">
                  <span className="text-slate-300">•</span>
                  <span>{ticket.resolution_time_hours.toFixed(1)}h to resolve</span>
                </div>
              )}
            </div>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="text-slate-400 hover:text-slate-600"
          >
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Expanded content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 border-t border-slate-100 pt-4">
              {/* Description */}
              {ticket.description && (
                <div className="mb-4">
                  <p className="text-sm text-slate-500 mb-2 font-medium">Description</p>
                  <p className="text-slate-700 text-sm leading-relaxed">
                    {ticket.description}
                  </p>
                </div>
              )}

              {/* Conversation History Toggle */}
              <div className="mb-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchConversations}
                  disabled={conversationsLoading}
                  className="border-slate-200 text-slate-600 hover:bg-slate-50 gap-2"
                >
                  {conversationsLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <MessageSquare className="w-4 h-4" />
                  )}
                  {showConversations ? "Hide" : "View"} Conversation History
                  {conversations.length > 0 && ` (${conversations.length})`}
                </Button>
              </div>

              {/* Conversation History */}
              <AnimatePresence>
                {showConversations && conversations.length > 0 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="mb-4 space-y-3 max-h-[300px] overflow-y-auto"
                  >
                    {conversations.map((conv, idx) => (
                      <div
                        key={conv.id || idx}
                        className={`p-3 rounded-lg text-sm ${
                          conv.type === "email"
                            ? conv.direction === "INCOMING_EMAIL"
                              ? "bg-blue-50 border border-blue-100"
                              : "bg-slate-50 border border-slate-100"
                            : "bg-amber-50 border border-amber-100"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          {conv.type === "email" ? (
                            <Mail className="w-3.5 h-3.5 text-blue-500" />
                          ) : (
                            <FileText className="w-3.5 h-3.5 text-amber-500" />
                          )}
                          <span className="font-medium text-slate-700">
                            {conv.type === "email"
                              ? conv.direction === "INCOMING_EMAIL"
                                ? "Customer Email"
                                : "Support Reply"
                              : "Internal Note"}
                          </span>
                          {conv.timestamp && (
                            <span className="text-xs text-slate-400 ml-auto">
                              {new Date(conv.timestamp).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        {conv.subject && (
                          <p className="text-xs text-slate-500 mb-1">
                            Subject: {conv.subject}
                          </p>
                        )}
                        <p className="text-slate-600 text-xs line-clamp-3">
                          {conv.body?.replace(/<[^>]*>/g, "").slice(0, 300)}
                          {conv.body?.length > 300 && "..."}
                        </p>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* AI Draft Section */}
              <div className="mt-4">
                {!draft ? (
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Button
                        onClick={handleGenerateDraft}
                        disabled={draftLoading}
                        className="bg-[#6B4EE6] hover:bg-[#5438C5] text-white gap-2 shadow-lg shadow-[#6B4EE6]/20"
                        data-testid={`generate-draft-${ticket.ticket_id}`}
                      >
                        {draftLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4" />
                            Generate AI Draft
                          </>
                        )}
                      </Button>
                      <span className="text-xs text-slate-400 self-center">
                        Uses conversation history & knowledge base
                      </span>
                    </div>
                    
                    {/* Direct Email (without AI draft) */}
                    {ticket.contact_email && (
                      <div className="pt-4 border-t border-slate-100">
                        {!showDirectEmail ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowDirectEmail(true)}
                            className="border-slate-200 text-slate-600 hover:bg-slate-50 gap-2"
                          >
                            <PenLine className="w-3.5 h-3.5" />
                            Write Email Directly
                          </Button>
                        ) : (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <PenLine className="w-4 h-4 text-[#425B76]" />
                              <span className="text-xs uppercase tracking-widest text-[#425B76] font-medium">
                                Direct Email
                              </span>
                            </div>
                            <Textarea
                              value={directEmailContent}
                              onChange={(e) => setDirectEmailContent(e.target.value)}
                              placeholder="Write your email message here..."
                              className="min-h-[120px] text-sm"
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={handleSendDirectEmail}
                                disabled={sending || !directEmailContent.trim()}
                                className="bg-[#425B76] hover:bg-[#2D4156] text-white gap-2"
                              >
                                {sending ? (
                                  <>
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    Sending...
                                  </>
                                ) : (
                                  <>
                                    <Send className="w-3.5 h-3.5" />
                                    Send Email
                                  </>
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setShowDirectEmail(false);
                                  setDirectEmailContent("");
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className={`rounded-lg p-4 ${draft.requires_human ? "bg-violet-50 border border-violet-200" : "bg-[#425B76]/5 border border-[#425B76]/20"}`}>
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      {draft.requires_human ? (
                        <>
                          <User className="w-4 h-4 text-violet-600" />
                          <span className="text-xs uppercase tracking-widest text-violet-600 font-medium">
                            Support Rep Required
                          </span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 text-[#425B76]" />
                          <span className="text-xs uppercase tracking-widest text-[#425B76] font-medium">
                            AI Draft Response
                          </span>
                          {draft.context_used?.conversation_history && (
                            <Badge variant="outline" className="text-[10px] border-blue-200 text-blue-600 gap-1">
                              <MessageSquare className="w-3 h-3" />
                              History
                            </Badge>
                          )}
                          {draft.context_used?.knowledge_base && (
                            <Badge variant="outline" className="text-[10px] border-green-200 text-green-600 gap-1">
                              <BookOpen className="w-3 h-3" />
                              KB
                            </Badge>
                          )}
                          <span className="ml-auto text-xs text-slate-500">
                            Confidence: {(draft.confidence * 100).toFixed(0)}%
                          </span>
                        </>
                      )}
                    </div>
                    
                    {/* Editable Draft Area */}
                    {isEditing ? (
                      <Textarea
                        value={editedDraft}
                        onChange={(e) => setEditedDraft(e.target.value)}
                        className="min-h-[150px] text-sm text-slate-700"
                        placeholder="Edit your email..."
                      />
                    ) : (
                      <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                        {draft.draft}
                      </p>
                    )}
                    
                    <div className="flex gap-2 mt-4 flex-wrap">
                      {!draft.sent && ticket.contact_email && (
                        <Button
                          size="sm"
                          onClick={() => handleSendEmail()}
                          disabled={sending || (draft.requires_human && !isEditing)}
                          className="bg-[#425B76] hover:bg-[#2D4156] text-white gap-2"
                          data-testid={`send-email-${ticket.ticket_id}`}
                        >
                          {sending ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-3.5 h-3.5" />
                              Send Email
                            </>
                          )}
                        </Button>
                      )}
                      {draft.sent && (
                        <Badge className="bg-emerald-100 text-emerald-700 gap-1">
                          <Check className="w-3 h-3" />
                          Email Sent
                        </Badge>
                      )}
                      
                      {/* Edit Button */}
                      {!draft.sent && !isEditing && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={startEditing}
                          className="border-slate-200 text-slate-600 hover:bg-slate-50 gap-2"
                        >
                          <Edit className="w-3.5 h-3.5" />
                          Edit
                        </Button>
                      )}
                      
                      {/* Cancel Edit Button */}
                      {isEditing && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={cancelEditing}
                          className="border-slate-200 text-slate-600 hover:bg-slate-50"
                        >
                          Cancel
                        </Button>
                      )}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyToClipboard}
                        className="border-slate-200 text-slate-600 hover:bg-slate-50 gap-2"
                      >
                        {copied ? (
                          <>
                            <Check className="w-3.5 h-3.5" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-3.5 h-3.5" />
                            Copy
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleGenerateDraft}
                        disabled={draftLoading}
                        className="border-slate-200 text-slate-600 hover:bg-slate-50 gap-2"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${draftLoading ? "animate-spin" : ""}`} />
                        Regenerate
                      </Button>
                      {!ticket.contact_email && (
                        <span className="text-xs text-amber-600 self-center">
                          No contact email available
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
